import type { Voyage, VoyageReport } from './types.js';

/**
 * Sefer özeti ekranının saf yardımcıları (`gemi-sefer-yonetimi.jpeg`; ADR 0022): sefer ağacı,
 * seyir günlüğü, iz ve özet değerleri. Hesapların kendisi `@felix/domain`'de (performans).
 */

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/** Saat → "3d 12h" (ekrandaki biçim). */
export function formatDuration(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return '—';
  const total = Math.round(hours);
  return `${Math.floor(total / 24)}d ${String(total % 24).padStart(2, '0')}h`;
}

/** Planlanan süre (saat) = planlanan mesafe / CP hızı. */
export function plannedHours(distanceNm: number, speedKn: number | null): number | null {
  return speedKn && speedKn > 0 ? Math.round((distanceNm / speedKn) * 100) / 100 : null;
}

export type TreeStage = 'departure' | 'enRoute' | 'arrival';

const STAGE: Record<VoyageReport['kind'], TreeStage> = {
  departure: 'departure',
  cosp: 'departure',
  noon: 'enRoute',
  anchorage_start: 'enRoute',
  anchorage_daily: 'enRoute',
  anchorage_end: 'enRoute',
  waiting_start: 'enRoute',
  waiting_end: 'enRoute',
  drift_start: 'enRoute',
  drift_daily: 'enRoute',
  drift_end: 'enRoute',
  strait_entry: 'enRoute',
  strait_exit: 'enRoute',
  canal_entry: 'enRoute',
  canal_exit: 'enRoute',
  on_hire: 'enRoute',
  off_hire: 'enRoute',
  eosp: 'arrival',
  arrival: 'arrival',
  port_daily: 'arrival',
  cargo_start: 'arrival',
  cargo_complete: 'arrival',
};

export interface TreeItem {
  key: string;
  kind: VoyageReport['kind'];
  /** Raporlar zamana göre; Noon özetinde aşamanın bütün Noon raporları. */
  reports: VoyageReport[];
  first: string;
  last: string;
  /** Özette gönderilmemiş rapor varsa `draft`. */
  status: VoyageReport['status'];
  /** Yalnız Noon özetinde: gönderilen / toplam. */
  count: { submitted: number; total: number } | null;
}

export interface TreeGroup {
  stage: TreeStage;
  items: TreeItem[];
  /** Son raporun aşaması: varsayılan olarak yalnız o açık (G4-38). */
  current: boolean;
}

/**
 * Sefer ağacı: Departure / Sailing, En Route, Arrival; her grupta raporlar zamana göre. Tekrarlanan
 * Noon raporları aşamada tek özet satırıdır ("Noon Reports 8/9"), ilk Noon'un yerinde (M6f G4-38).
 */
export function voyageTree(reports: readonly VoyageReport[]): TreeGroup[] {
  const sorted = [...reports].sort(byTime);
  const latest = sorted.at(-1);
  return (['departure', 'enRoute', 'arrival'] as const).map((stage) => {
    const items: TreeItem[] = [];
    let noons: TreeItem | null = null;
    for (const report of sorted.filter((r) => STAGE[r.kind] === stage)) {
      if (report.kind === 'noon') {
        if (!noons) {
          noons = {
            key: `${stage}-noons`,
            kind: 'noon',
            reports: [],
            first: report.reportedAt,
            last: report.reportedAt,
            status: 'submitted',
            count: { submitted: 0, total: 0 },
          };
          items.push(noons);
        }
        noons.reports.push(report);
        noons.last = report.reportedAt;
        const count = noons.count as { submitted: number; total: number };
        count.total += 1;
        if (report.status === 'submitted') count.submitted += 1;
        else noons.status = 'draft';
        continue;
      }
      items.push({
        key: report.id,
        kind: report.kind,
        reports: [report],
        first: report.reportedAt,
        last: report.reportedAt,
        status: report.status,
        count: null,
      });
    }
    return { stage, items, current: !!latest && STAGE[latest.kind] === stage };
  });
}

/**
 * Gecikme (saat): gönderilmiş varış raporu ile planlanan ETA farkı; artı geç, eksi erken. Sefer
 * sürüyorsa hesaplanmaz (tahmini varış veri modelinde yok; G4-42).
 */
export function voyageDelayHours(
  plannedEta: string,
  reports: readonly VoyageReport[],
): number | null {
  const arrival = [...reports]
    .filter((r) => r.kind === 'arrival' && r.status === 'submitted')
    .sort(byTime)
    .at(-1);
  if (!arrival) return null;
  return (
    Math.round(((Date.parse(arrival.reportedAt) - Date.parse(plannedEta)) / 3_600_000) * 100) / 100
  );
}

/**
 * Hız göstergesinin dolu oranı (%): ortalama hız / (CP hızı × 1,2), en çok 100. CP hızı yayın
 * %83'ündedir; gösterge hep dolu görünmez (G4-43). Yalnız görsel.
 */
export function speedGaugePct(avgKn: number | null, plannedKn: number | null): number | null {
  if (avgKn === null || plannedKn === null || plannedKn <= 0) return null;
  return Math.min(100, Math.round((avgKn / (plannedKn * 1.2)) * 100));
}

export interface CpStatusRow {
  key: 'speed' | 'distance' | 'time';
  /** Uygun mu; değer yoksa `null`. */
  ok: boolean | null;
}

/**
 * Speed & CP Status satırları (G4-46): ortalama hız CP hızının altında değil; gidilen mesafe
 * planlanan mesafeyi ve geçen süre planlanan süreyi aşmıyor. Tolerans yok (en sıkı okuma,
 * `cpComparison` ile aynı).
 */
export function cpStatusRows(values: {
  plannedSpeedKn: number | null;
  avgSpeedKn: number | null;
  plannedDistanceNm: number;
  distanceNm: number | null;
  plannedHours: number | null;
  hours: number | null;
}): CpStatusRow[] {
  const atLeast = (actual: number | null, limit: number | null) =>
    actual === null || limit === null ? null : actual >= limit;
  const atMost = (actual: number | null, limit: number | null) =>
    actual === null || limit === null ? null : actual <= limit;
  return [
    { key: 'speed', ok: atLeast(values.avgSpeedKn, values.plannedSpeedKn) },
    { key: 'distance', ok: atMost(values.distanceNm, values.plannedDistanceNm) },
    { key: 'time', ok: atMost(values.hours, values.plannedHours) },
  ];
}

const num = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Seyir günlüğü satırı: hız (SOG) ve son rapordan bu yana mesafe varsa. */
export function logRows(reports: readonly VoyageReport[]) {
  return [...reports].sort(byTime).map((r) => {
    const source = (r.kind === 'noon' ? r.data.navigation : r.data) as
      | Record<string, unknown>
      | undefined;
    return {
      id: r.id,
      kind: r.kind,
      reportedAt: r.reportedAt,
      tzOffsetMin: r.tzOffsetMin,
      speedKn: num(source?.sog),
      distanceNm: num(source?.distanceSinceLast),
      status: r.status,
    };
  });
}

/** İz: konumu olan raporlar, zamana göre (çevrimdışı gemi için harita yerine çizgi). */
export function track(reports: readonly VoyageReport[]) {
  return [...reports]
    .sort(byTime)
    .filter((r) => r.lat !== null && r.lon !== null)
    .map((r) => ({ kind: r.kind, lat: r.lat as number, lon: r.lon as number }));
}

/** CP uyumu (%): gerçek ortalama hızın CP hızına oranı, en çok %100 (çubuk). */
export function cpCompliancePct(actualKn: number | null, plannedKn: number | null): number | null {
  if (actualKn === null || plannedKn === null || plannedKn <= 0) return null;
  return Math.min(100, Math.round((actualKn / plannedKn) * 100));
}

/**
 * Raporsuz seferin boş durumu (ortağın notu #1; ADR 0047 §1): gemide "Create Departure Report"
 * (Leg / Event'i o seferle açar), ofiste "rapor gemiden gelir". Raporu olan, tamamlanan ya da
 * iptal edilen seferde eylem yok.
 */
export function emptyLogAction(
  status: Voyage['status'],
  reports: readonly VoyageReport[],
  editing: boolean,
  /** Bu sefere bağlanabilecek sefersiz rapor sayısı (ADR 0157 §3, #2266). */
  queued = 0,
): 'createDeparture' | 'linkFirst' | 'awaitShip' | null {
  if (reports.length > 0 || (status !== 'planned' && status !== 'active')) return null;
  // Raporsuz sefere ilk rapor girilecekken sefersiz raporlar varsa önce bağlama önerilir
  if (editing && queued > 0) return 'linkFirst';
  return editing ? 'createDeparture' : 'awaitShip';
}

/** Seferin Leg / Event bağlantısı; tür verilirse o türde yeni olay açılır. */
export function legEventPath(voyageId: string, kind?: string): string {
  const query = new URLSearchParams({ voyage: voyageId, ...(kind ? { kind } : {}) });
  return `/voyage/legs?${query.toString()}`;
}
